import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, Calculator, CheckCircle2, Loader2, MapPin, MessageSquare, Navigation, Pencil, Plane, Printer,
  Send, Share2, Undo2, XCircle,
} from "lucide-react";
import api, { PLATFORM_NAME, errMsg, money } from "@/lib/api";
import { STAGES, clock, stageIndex, stageLabel } from "@/lib/lifecycle";
import { StageBadge } from "@/components/StageBadge";
import { ChatPanel } from "@/components/ChatPanel";
import { ChangeRequestCard } from "@/components/ChangeRequestCard";
import { DriverPayCard } from "@/components/DriverPayCard";
import { ReofferCard } from "@/components/ReofferCard";
import { PaymentLinkCard } from "@/components/PaymentLinkCard";
import { QuoteBreakdown } from "@/components/QuoteBreakdown";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusPill } from "@/pages/Manifests";

const Row = ({ label, value }) =>
  value ? (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{label}</p>
      <p className="mt-1 text-sm font-semibold text-neutral-900">{value}</p>
    </div>
  ) : null;

export default function ManifestView() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [sending, setSending] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [invoiceId, setInvoiceId] = useState(null);
  const [actuals, setActuals] = useState({ actual_hours: "", actual_miles: "", waiting_minutes: "", overtime_hours: "" });
  const [delayMinutes, setDelayMinutes] = useState("");
  const [partners, setPartners] = useState([]);
  const [farmOut, setFarmOut] = useState({ partner_ids: [], payout: "" });

  const load = () =>
    api.get(`/manifests/${id}`).then(({ data }) => setData(data)).catch((e) => setError(errMsg(e)));

  useEffect(() => {
    load();
    api.get("/partners").then(({ data }) => setPartners(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // The chauffeur accepting or declining has to show up here without a manual refresh.
  useEffect(() => {
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const act = async (fn, setter, msg) => {
    setter(true);
    setError("");
    setNotice("");
    try {
      const res = await fn();
      setNotice(typeof msg === "function" ? msg(res) : msg);
      await load();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setter(false);
    }
  };

  if (!data) {
    return (
      <div className="space-y-4">
        {error ? <p data-testid="manifest-view-error" className="text-sm text-red-400">{error}</p>
          : <p className="text-sm text-slate-500">Loading manifest…</p>}
      </div>
    );
  }

  const { manifest: m, company: c } = data;
  const stops = m.stops || [];
  const pendingStops = stops.filter((s) => s.status === "pending");

  return (
    <div data-testid="manifest-view" className="space-y-6">
      <div className="no-print flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <Link to="/app/manifests" data-testid="back-to-manifests" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-gold">
          <ArrowLeft className="h-4 w-4" /> All trips
        </Link>
        <div className="flex flex-wrap gap-2">
          <StatusPill status={m.status} />
          <Button data-testid="print-manifest-btn" variant="outline" className="border-ink-600 bg-transparent text-slate-300 hover:text-gold" onClick={() => window.print()}>
            <Printer className="mr-2 h-4 w-4" /> Print / PDF
          </Button>
          <Button asChild variant="outline" className="border-ink-600 bg-transparent text-slate-300 hover:text-gold">
            <Link to={`/app/manifests/${m.id}/edit`} data-testid="edit-manifest-btn"><Pencil className="mr-2 h-4 w-4" /> Edit</Link>
          </Button>
          <Button data-testid="send-to-driver-btn" disabled={sending}
            onClick={() => act(async () => (await api.post(`/manifests/${id}/send-to-driver`)).data, setSending,
              (res) => `Manifest emailed to the chauffeur. Trip link: ${res.trip_url}`)}
            className="bg-gold text-ink-900 hover:bg-gold-light">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Send className="mr-2 h-4 w-4" /> Send to driver</>)}
          </Button>
          {m.status !== "completed" && (
            <Button data-testid="complete-trip-btn" disabled={completing} variant="outline"
              onClick={() => act(async () => {
                const { data: res } = await api.post(`/manifests/${id}/complete`);
                setInvoiceId(res.invoice?.id || null);
                return res;
              }, setCompleting, (res) => `Trip completed. Invoice #${res.invoice?.number} issued and emailed.`)}
              className="border-emerald-500/40 bg-transparent text-emerald-300 hover:bg-emerald-500/10">
              {completing ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><CheckCircle2 className="mr-2 h-4 w-4" /> Complete & invoice</>)}
            </Button>
          )}
        </div>
      </div>

      {notice && (
        <p data-testid="send-success" className="no-print break-all rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-300">
          {notice}{" "}
          {invoiceId && <Link to={`/app/invoices/${invoiceId}`} data-testid="view-invoice-link" className="underline">View invoice</Link>}
        </p>
      )}
      {error && <p data-testid="send-error" className="no-print text-sm text-red-400">{error}</p>}

      {m.stage === "declined" && <ReofferCard trip={m} onDone={load} />}
      <DriverPayCard trip={m} onSaved={load} />
      <ChangeRequestCard manifestId={id} trip={m} onDone={(decision) => {
        setNotice(decision === "approve"
          ? "Pickup moved to the time the passenger asked for — everyone notified."
          : "Request declined. The passenger has been told the pickup stands.");
        load();
      }} />
      {m.stage === "driver_accepted" && (
        <p data-testid="driver-accepted-note" className="no-print flex items-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-4 text-sm text-emerald-200">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          {m.chauffeur_name || "The chauffeur"} accepted this job
          {m.stage_at?.driver_accepted ? ` at ${new Date(m.stage_at.driver_accepted).toLocaleTimeString()}` : ""}.
        </p>
      )}

      <div className="no-print grid grid-cols-1 gap-6">
        <ChatPanel mine="operator" title="Trip messages (passenger &amp; chauffeur)"
          listUrl={`/manifests/${id}/messages`} sendUrl={`/manifests/${id}/messages`} />
        <div data-testid="final-calculation-card" className="rounded-xl border border-ink-600 bg-ink-800 p-6">
          <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Final trip calculation</p>
          <div className="mt-4 grid grid-cols-2 gap-3">
            {[
              ["actual_hours", "Actual hours"],
              ["actual_miles", "Actual miles"],
              ["waiting_minutes", "Waiting minutes"],
              ["overtime_hours", "Overtime hours"],
            ].map(([k, label]) => (
              <div key={k}>
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</label>
                <Input data-testid={`final-${k}`} type="number" step="0.1" value={actuals[k]}
                  onChange={(e) => setActuals({ ...actuals, [k]: e.target.value })}
                  className="h-10 border-ink-600 bg-ink-900 text-slate-100 focus-visible:ring-gold" />
              </div>
            ))}
          </div>
          <Button data-testid="finalize-btn" disabled={finalizing} className="mt-4 bg-gold text-ink-900 hover:bg-gold-light"
            onClick={() => act(async () => (await api.post(`/manifests/${id}/finalize`, {
              actual_hours: Number(actuals.actual_hours || 0),
              actual_miles: Number(actuals.actual_miles || 0),
              waiting_minutes: Number(actuals.waiting_minutes || 0),
              overtime_hours: Number(actuals.overtime_hours || 0),
            })).data, setFinalizing, (res) => `Final total ${money(res.quote.total)} · balance due ${money(res.balance_due)}.`)}>
            {finalizing ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Calculator className="mr-2 h-4 w-4" /> Recalculate final price</>)}
          </Button>
          {m.final_quote && <div className="mt-4"><QuoteBreakdown quote={m.final_quote} testId="final-quote" label="Final calculation" /></div>}
          {(m.wait_detail || []).length > 0 && (
            <div data-testid="wait-detail" className="mt-4 rounded-lg border border-ink-600 bg-ink-900/60 p-4">
              <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Metered wait &amp; overtime</p>
              <div className="mt-3 space-y-2">
                {m.wait_detail.map((w, i) => (
                  <div key={i} className="flex flex-wrap items-baseline justify-between gap-2 text-xs">
                    <span className="text-slate-300">{w.label}<span className="text-slate-500"> · {w.note}</span></span>
                    <span className="font-mono text-slate-400">
                      {w.minutes} min − {w.grace} min grace =
                      <span className={w.billable_minutes > 0 ? " text-gold" : " text-slate-500"}> {w.billable_minutes} billable</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-6 border-t border-ink-600 pt-4">
            <div><p className="text-xs text-slate-500">Paid so far</p><p className="font-display text-lg text-slate-50">{money(m.amount_paid)}</p></div>
            {m.tip_amount > 0 && (
              <div><p className="text-xs text-slate-500">Gratuity</p><p data-testid="final-gratuity" className="font-display text-lg text-gold">{money(m.tip_amount)}</p></div>
            )}
            <div>
              <p className="text-xs text-slate-500">Still to collect</p>
              <p data-testid="balance-due" className="font-display text-lg text-slate-50">
                {money(Math.max(0, Number(m.final_total || m.total_charge || 0)
                  + Number(m.tip_amount || 0) - Number(m.amount_paid || 0)))}
              </p>
            </div>
            {m.deposit_due > 0 && !m.amount_paid && (
              <div>
                <p className="text-xs text-slate-500">Of which due at reservation</p>
                <p data-testid="deposit-due" className="font-display text-lg text-slate-300">{money(m.deposit_due)}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <PaymentLinkCard manifestId={m.id} />

      {(m.flight_number || m.track_token) && (        <div data-testid="flight-tracking-card" className="no-print rounded-xl border border-ink-600 bg-ink-800 p-6">
          <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Flight tracking & passenger link</p>
          <div className="mt-4 flex flex-wrap items-end gap-6">
            {m.flight_number && (
              <div>
                <p className="text-xs text-slate-500">Flight</p>
                <p className="text-sm text-slate-100">{[m.airline, m.flight_number].filter(Boolean).join(" ")}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-slate-500">Status</p>
              <p data-testid="flight-status" className="text-sm text-slate-100">
                {m.flight_status
                  ? `${m.flight_status.status || "scheduled"}${m.flight_delay_minutes ? ` · ${m.flight_delay_minutes} min late` : ""}`
                  : "Not checked yet"}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Pickup time</p>
              <p className="text-sm text-slate-100">
                {m.pickup_time}
                {m.original_pickup_time && m.original_pickup_time !== m.pickup_time
                  ? ` (was ${m.original_pickup_time})` : ""}
              </p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-end gap-3">
            {m.flight_number && (
              <Button data-testid="check-flight-btn" disabled={finalizing} variant="outline"
                className="border-ink-600 bg-transparent text-slate-300 hover:text-gold"
                onClick={() => act(async () => (await api.get(`/manifests/${id}/flight`)).data, setFinalizing,
                  (res) => res.adjusted
                    ? `Flight is ${res.flight.delay_minutes} min late — pickup moved and everyone notified.`
                    : `Flight status: ${res.flight.status || "scheduled"}${res.flight.delay_minutes ? ` · ${res.flight.delay_minutes} min late` : " · on time"}.`)}>
                <Plane className="mr-2 h-4 w-4" /> Check flight now
              </Button>
            )}
            <div>
              <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Report delay (min)</label>
              <Input data-testid="manual-delay-input" type="number" min="1" value={delayMinutes}
                onChange={(e) => setDelayMinutes(e.target.value)}
                className="h-10 w-32 border-ink-600 bg-ink-900 text-slate-100 focus-visible:ring-gold" />
            </div>
            <Button data-testid="apply-delay-btn" disabled={finalizing || !delayMinutes}
              className="bg-gold text-ink-900 hover:bg-gold-light"
              onClick={() => act(async () => (await api.post(`/manifests/${id}/flight-delay`,
                { delay_minutes: Number(delayMinutes) })).data, setFinalizing,
                (res) => `Pickup shifted by ${res.delay_minutes} minutes and everyone notified.`)}>
              Shift pickup
            </Button>
            <Button data-testid="passenger-track-link-btn" variant="outline"
              className="border-gold/40 bg-transparent text-gold hover:bg-gold/10 hover:text-gold"
              onClick={() => act(async () => (await api.post(`/manifests/${id}/track-link`)).data, setFinalizing,
                (res) => `Passenger tracking link: ${res.url}`)}>
              <Navigation className="mr-2 h-4 w-4" /> Passenger tracking link
            </Button>
            <Button data-testid="email-tracking-btn" variant="outline"
              className="border-ink-600 bg-transparent text-slate-300 hover:text-gold"
              onClick={() => act(async () => (await api.post(`/manifests/${id}/email-tracking`)).data, setFinalizing,
                (res) => `Tracking link emailed to ${res.to}.`)}>
              <MessageSquare className="mr-2 h-4 w-4" /> Email tracking link
            </Button>
          </div>
        </div>
      )}

      <div data-testid="lifecycle-card" className="no-print rounded-xl border border-ink-600 bg-ink-800 p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Trip status</p>
          <StageBadge stage={m.stage || "booked"} testId="manifest-stage" />
        </div>
        {m.stage === "no_show" ? (
          <div data-testid="no-show-summary" className="mt-4 rounded-lg border border-amber-400/40 bg-amber-400/10 p-4">
            <p className="text-sm font-semibold text-amber-200">
              This trip ended as a passenger no-show{m.no_show?.waited_minutes != null
                ? ` after ${m.no_show.waited_minutes} min on scene` : ""}.
            </p>
            <p className="mt-1.5 font-mono text-xs text-amber-100/80">
              No-show fee {money(m.no_show?.fee)}
              {m.no_show?.wait_charge > 0 ? ` + waiting ${money(m.no_show.wait_charge)}` : ""}
              {" = "}{money(m.no_show?.total ?? m.total_charge)} billed
              {m.no_show?.mode ? ` · ${m.no_show.mode} policy` : ""}
            </p>
            {m.no_show?.by && (
              <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-amber-100/60">
                Marked by {m.no_show.by}{m.no_show.note ? ` · ${m.no_show.note}` : ""}
              </p>
            )}
          </div>
        ) : (
          <div className="mt-4 flex flex-wrap gap-2">
            {STAGES.slice(1).map((s) => {
              const done = stageIndex(m.stage || "booked") >= stageIndex(s);
              return (
                <span key={s} data-testid={`stage-step-${s}`}
                  className={`rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest ${
                    done ? "border-gold/40 bg-gold/10 text-gold" : "border-ink-600 text-slate-500"
                  }`}>
                  {stageLabel(s)}
                </span>
              );
            })}
          </div>
        )}
        {(m.timeline || []).length > 0 && (
          <div className="mt-5 space-y-1.5 border-t border-ink-600 pt-4">
            {m.timeline.map((e, i) => (
              <p key={i} data-testid={`timeline-entry-${i}`} className="flex flex-wrap gap-2 text-xs text-slate-400">
                <span className="text-slate-200">{stageLabel(e.stage)}</span>
                <span className="font-mono text-[10px] text-slate-500">
                  {new Date(e.at).toLocaleString()} · {e.by_name || e.by_role}
                </span>
              </p>
            ))}
          </div>
        )}
        {(m.recorded_minutes || m.recorded_miles) ? (
          <p data-testid="recorded-trip" className="mt-4 font-mono text-xs text-slate-300">
            Recorded {clock(m.recorded_minutes)} · {(m.recorded_miles || 0).toFixed(1)} mi driven with the passenger onboard
          </p>
        ) : null}
        {m.driver_location ? (
          <a href={`https://www.google.com/maps?q=${m.driver_location.lat},${m.driver_location.lng}`}
            target="_blank" rel="noopener noreferrer" data-testid="driver-location-link"
            className="mt-4 inline-flex items-center gap-2 font-mono text-xs text-gold">
            <Navigation className="h-3.5 w-3.5" />
            {Number(m.driver_location.lat).toFixed(4)}, {Number(m.driver_location.lng).toFixed(4)} · view on map
          </a>
        ) : (
          <p className="mt-4 font-mono text-xs text-slate-500">No GPS ping from the chauffeur yet.</p>
        )}
        <div className="mt-5 flex flex-wrap gap-2">
          {m.stage === "booked" && m.chauffeur_name && (
            <Button data-testid="stage-assign-btn" disabled={finalizing} variant="outline"
              className="border-ink-600 bg-transparent text-slate-300 hover:text-gold"
              onClick={() => act(async () => (await api.post(`/trips/${id}/advance`, { stage: "assigned" })).data,
                setFinalizing, "Trip marked as assigned — the chauffeur can accept it now.")}>
              Mark as assigned
            </Button>
          )}
          {m.stage === "assigned" && (
            <Button data-testid="stage-accept-for-driver-btn" disabled={finalizing} variant="outline"
              className="border-ink-600 bg-transparent text-slate-300 hover:text-gold"
              onClick={() => act(async () => (await api.post(`/trips/${id}/advance`, { stage: "driver_accepted" })).data,
                setFinalizing, "Accepted on the chauffeur's behalf.")}>
              Accept for the chauffeur
            </Button>
          )}
          {m.stage === "arrived_destination" && (
            <Button data-testid="stage-complete-btn" disabled={finalizing}
              className="bg-gold text-ink-900 hover:bg-gold-light"
              onClick={() => act(async () => (await api.post(`/trips/${id}/advance`, { stage: "completed" })).data,
                setFinalizing, "Trip completed.")}>
              Complete trip
            </Button>
          )}
          {m.stage === "no_show" && (
            <Button data-testid="no-show-reverse-btn" disabled={finalizing} variant="outline"
              className="border-amber-400/40 bg-transparent text-amber-200 hover:bg-amber-400/10"
              onClick={() => act(async () => (await api.post(`/trips/${id}/no-show/reverse`, { note: "" })).data.trip,
                setFinalizing, "No-show reversed — the trip is back at the pickup and the old price restored.")}>
              Reverse this no-show
            </Button>
          )}
          {m.stage === "completed" && (
            <Button data-testid="stage-finalize-btn" disabled={finalizing}
              className="bg-gold text-ink-900 hover:bg-gold-light"
              onClick={() => act(async () => (await api.post(`/trips/${id}/advance`, { stage: "payment_finalized" })).data,
                setFinalizing, "Payment marked as finalized.")}>
              Mark payment finalized
            </Button>
          )}
        </div>
      </div>

      <div data-testid="farm-out-card" className="no-print rounded-xl border border-ink-600 bg-ink-800 p-6">
        <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Farm this trip out</p>
        {m.farm_in_from ? (
          <p className="mt-3 text-sm text-slate-300">This trip came to you from {m.farm_in_from}.</p>
        ) : m.farm_status ? (
          <div className="mt-3 space-y-3">
            <p data-testid="farm-status" className="text-sm text-slate-300">
              Offered to {m.farmed_out_to} for {money(m.farm_payout)} · <span className="text-gold">{m.farm_status}</span>
            </p>
            {m.partner_driver_name && (
              <p data-testid="farm-partner-driver" className="text-xs text-slate-400">
                Their chauffeur: {m.partner_driver_name}
                {m.partner_vehicle_name ? ` · ${m.partner_vehicle_name}` : ""} — their progress shows on this trip and on your passenger's tracking link.
              </p>
            )}
            {m.farm_status === "offered" && (
              <Button data-testid="farm-recall-btn" variant="outline" disabled={finalizing}
                className="border-ink-600 bg-transparent text-slate-300 hover:text-gold"
                onClick={() => act(async () => (await api.post(`/manifests/${id}/recall-offers`)).data,
                  setFinalizing, "Offer pulled back — this trip is yours again.")}>
                <Undo2 className="mr-2 h-4 w-4" /> Pull the offer back
              </Button>
            )}
          </div>
        ) : partners.length === 0 ? (
          <p className="mt-3 text-sm text-slate-400">
            Add a partner operator in <Link to="/app/affiliates" className="text-gold underline">Affiliates</Link> to
            hand overflow trips out.
          </p>
        ) : (
          <div className="mt-4 space-y-4">
            <div>
              <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                Offer to {farmOut.partner_ids.length || "one or more"} partner{farmOut.partner_ids.length === 1 ? "" : "s"} · first to accept keeps it
              </p>
              <div className="flex flex-wrap gap-2">
                {partners.map((p) => {
                  const picked = farmOut.partner_ids.includes(p.id);
                  return (
                    <button key={p.id} type="button" data-testid={`farm-out-partner-${p.id}`}
                      onClick={() => setFarmOut({
                        ...farmOut,
                        partner_ids: picked ? farmOut.partner_ids.filter((x) => x !== p.id)
                                            : [...farmOut.partner_ids, p.id],
                      })}
                      className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
                        picked ? "border-gold bg-gold/15 text-gold"
                               : "border-neutral-300 text-neutral-600 hover:border-gold/60"}`}>
                      {p.partner_name}
                      {p.rating ? ` · ★${p.rating}` : ""}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-neutral-500">Their payout</label>
                <Input data-testid="farm-out-payout" type="number" step="0.01" value={farmOut.payout}
                  onChange={(e) => setFarmOut({ ...farmOut, payout: e.target.value })}
                  className="h-10 w-32 border-ink-600 bg-ink-900 text-slate-100 focus-visible:ring-gold" />
              </div>
              <Button data-testid="farm-out-btn" disabled={finalizing || !farmOut.partner_ids.length || !farmOut.payout}
                className="bg-gold text-ink-900 hover:bg-gold-light"
                onClick={() => act(async () => (await api.post(`/manifests/${id}/farm-out`, {
                  partner_ids: farmOut.partner_ids, payout: Number(farmOut.payout),
                })).data, setFinalizing, "Job offered — whoever accepts first takes the trip.")}>
                <Share2 className="mr-2 h-4 w-4" /> Farm out
              </Button>
            </div>
          </div>
        )}
      </div>

      {stops.length > 0 && (
        <div data-testid="stops-card" className="no-print rounded-xl border border-ink-600 bg-ink-800 p-6">
          <p className="font-mono text-[11px] uppercase tracking-widest text-gold">
            Itinerary stops {pendingStops.length > 0 && `· ${pendingStops.length} awaiting approval`}
          </p>
          <div className="mt-4 space-y-3">
            {stops.map((s, i) => (
              <div key={i} data-testid={`stop-row-${i}`} className="flex flex-wrap items-center justify-between gap-3 border-b border-ink-600 pb-3 last:border-0">
                <span className="flex items-center gap-2 text-sm text-slate-200">
                  <MapPin className="h-4 w-4 text-gold" /> {s.address}
                  <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                    {s.status}{s.requested_by ? ` · by ${s.requested_by}` : ""}
                  </span>
                  {s.charge > 0 && (
                    <span data-testid={`stop-charge-${i}`} className="font-mono text-[10px] uppercase tracking-widest text-gold">
                      · billed {money(s.charge)}
                    </span>
                  )}
                </span>
                {s.status === "pending" && (
                  <span className="flex gap-2">
                    <Button size="sm" data-testid={`stop-approve-${i}`} className="bg-emerald-500 text-ink-900 hover:bg-emerald-400"
                      onClick={() => act(async () => (await api.post(`/manifests/${id}/stops/${i}/approve`)).data, setFinalizing, "Stop approved and sent to the chauffeur.")}>
                      <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" data-testid={`stop-decline-${i}`} className="border-red-500/40 bg-transparent text-red-300"
                      onClick={() => act(async () => (await api.post(`/manifests/${id}/stops/${i}/decline`)).data, setFinalizing, "Stop declined.")}>
                      <XCircle className="mr-1.5 h-3.5 w-3.5" /> Decline
                    </Button>
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="print-sheet mx-auto max-w-3xl overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="px-8 py-7" style={{ background: c.branding?.accent_color || "#0F131C" }}>
          <div className="flex items-start justify-between gap-6">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.28em]" style={{ color: c.branding?.primary_color || "#D4AF37" }}>
                {c.name}
              </p>
              <p className="mt-2 font-display text-2xl font-bold text-white">TRIP MANIFEST #{m.number}</p>
              <p className="mt-1 text-xs text-neutral-400">
                {c.address}{c.address && c.phone ? " · " : ""}{c.phone}{c.email ? ` · ${c.email}` : ""}
              </p>
            </div>
            {c.logo_url ? (
              <img src={c.logo_url} alt={`${c.name} logo`} className="h-14 w-auto object-contain" />
            ) : (
              <div className="flex h-14 w-14 items-center justify-center rounded border border-white/20 font-mono text-[10px] text-neutral-400">LOGO</div>
            )}
          </div>
        </div>

        <div className="space-y-7 p-8">
          <div className="grid grid-cols-2 gap-6 sm:grid-cols-3">
            <Row label="Customer" value={m.customer_name} />
            <Row label="Phone" value={m.customer_phone} />
            <Row label="Email" value={m.customer_email} />
          </div>
          <div className="grid grid-cols-1 gap-6 border-t border-neutral-200 pt-6 sm:grid-cols-2">
            <Row label="Pickup" value={m.pickup?.address} />
            <Row label="Drop-off" value={m.dropoff?.address} />
          </div>
          {stops.filter((s) => s.status === "approved").length > 0 && (
            <div className="border-t border-neutral-200 pt-6">
              <Row label="Stops" value={stops.filter((s) => s.status === "approved").map((s) => s.address).join(" → ")} />
            </div>
          )}
          <div className="grid grid-cols-2 gap-6 border-t border-neutral-200 pt-6 sm:grid-cols-4">
            <Row label="Airline" value={m.airline} />
            <Row label="Flight" value={m.flight_number} />
            <Row label="Terminal" value={m.terminal} />
            <Row label="Date" value={m.trip_date} />
          </div>
          <div className="grid grid-cols-2 gap-6 border-t border-neutral-200 pt-6 sm:grid-cols-4">
            <Row label="Pickup time" value={m.pickup_time} />
            <Row label="Vehicle" value={m.vehicle_name} />
            <Row label="Chauffeur" value={m.chauffeur_name} />
            <Row label="Pricing" value={m.pricing_mode === "hourly" ? `Hourly · ${m.hours_booked || 0} h` : `Mileage · ${(m.quote?.miles ?? m.actual_miles) || 0} mi`} />
          </div>
          {m.special_instructions && (
            <div className="border-t border-neutral-200 pt-6"><Row label="Special instructions" value={m.special_instructions} /></div>
          )}
          <div className="flex flex-wrap items-end justify-between gap-6 border-t border-neutral-200 pt-6">
            <div className="flex gap-8">
              <Row label="At reservation" value={money(m.deposit_due)} />
              <Row label="Balance" value={money(m.balance_due)} />
            </div>
            <div className="text-right">
              <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                {m.final_total ? "Final total" : "Estimated total"}
              </p>
              <p data-testid="manifest-total-amount" className="font-display text-3xl font-bold text-neutral-900">
                {money(m.final_total || m.total_charge)}
              </p>
            </div>
          </div>
          <div className="border-t border-neutral-200 pt-5">
            <p className="text-[10px] leading-relaxed text-neutral-500">
              <strong>Transportation Service Disclaimer</strong> — This transportation service is provided by{" "}
              {c.name}, the transportation operator identified on this reservation. The software platform
              facilitates communication, scheduling, dispatch and payment technology but does not provide or
              operate the transportation service.
            </p>
            {c.show_powered_by && (
              <p data-testid="manifest-powered-by" className="mt-3 font-mono text-[10px] text-neutral-400">Powered by {PLATFORM_NAME}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
